import type { CSSProperties, ReactNode } from 'react';

type Provider = 'google' | 'microsoft' | 'zoho' | 'email';

const providerIcon: Record<Provider, ReactNode> = {
  google: (
    <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
  ),
  microsoft: (
    <svg width="18" height="18" viewBox="0 0 23 23"><rect x="1" y="1" width="10" height="10" fill="#f25022"/><rect x="12" y="1" width="10" height="10" fill="#7fba00"/><rect x="1" y="12" width="10" height="10" fill="#00a4ef"/><rect x="12" y="12" width="10" height="10" fill="#ffb900"/></svg>
  ),
  zoho: (
    <svg width="20" height="20" viewBox="0 0 24 24"><rect width="24" height="24" rx="5" fill="#1B9A59"/><text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff" fontFamily="system-ui">Z</text></svg>
  ),
  email: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
  ),
};

const buttonStyle: CSSProperties = {
  padding: '13px 16px',
  borderRadius: 999,
  border: '1px solid var(--color-hairline-strong)',
  background: 'var(--color-surface)',
  color: 'var(--color-ink)',
  fontSize: 15,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background 120ms, border-color 120ms',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
};

interface OAuthBtnProps {
  provider: Provider;
  children: ReactNode;
  href?: string;
  onClick?: () => void;
}

function OAuthBtn({ provider, children, href, onClick }: OAuthBtnProps) {
  const onMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.background = 'var(--color-bg-alt)';
  };
  const onMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.background = 'var(--color-surface)';
  };
  if (href) {
    return (
      <a href={href} style={buttonStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {providerIcon[provider]}
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} style={buttonStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {providerIcon[provider]}
      {children}
    </button>
  );
}

interface OAuthStackProps {
  onEmailClick: () => void;
}

export default function OAuthStack({ onEmailClick }: OAuthStackProps) {
  return (
    <div className="flex flex-col gap-2.5 mb-6">
      <OAuthBtn provider="google" href="/auth/google">Continue with Google</OAuthBtn>
      <OAuthBtn provider="microsoft" href="/auth/microsoft">Continue with Microsoft</OAuthBtn>
      <OAuthBtn provider="zoho" href="/auth/zoho">Continue with Zoho</OAuthBtn>
      <OAuthBtn provider="email" onClick={onEmailClick}>Continue with Email</OAuthBtn>
    </div>
  );
}
